import { useState } from 'react';
import { dppApi, productsApi } from '../api/client';
import type { DppRecord, Product } from '../api/client';
import { useApi } from '../hooks/useApi';
import { T, SectionHeader, Card, Chip, Skeleton, ErrorBox, ScoreRing } from '../components/ui';

const STATUS_C: Record<string, string> = {
  published: T.sage, draft: T.amber, missing: T.crimson, superseded: T.dim,
};

export default function DppPage() {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [dppDetail,   setDppDetail]   = useState<DppRecord | null>(null);
  const [generating,  setGenerating]  = useState(false);
  const [publishing,  setPublishing]  = useState(false);
  const [genError,    setGenError]    = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const { data: dppsData, loading: dppsLoading, refetch: refetchDpps } = useApi(
    () => dppApi.list(), []
  );
  const { data: productsData } = useApi(() => productsApi.list({ limit: 100 }), []);

  const dpps    = dppsData?.dpps || [];
  const products = productsData?.products || [];

  // Products without DPPs
  const dppProductIds = new Set(dpps.map(d => d.product_id));
  const missingDpps   = products.filter(p => !dppProductIds.has(p.id));

  const filteredDpps = filterStatus === 'all' ? dpps
    : filterStatus === 'missing' ? []
    : dpps.filter(d => d.status === filterStatus);

  const selectProduct = async (product: Product) => {
    setSelectedProduct(product);
    setGenError('');
    const existing = dpps.find(d => d.product_id === product.id);
    setDppDetail(existing || null);
  };

  const generate = async () => {
    if (!selectedProduct) return;
    setGenerating(true); setGenError('');
    try {
      const result = await dppApi.generate(selectedProduct.id);
      setDppDetail(result);
      refetchDpps();
    } catch (err) {
      setGenError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const publish = async () => {
    if (!selectedProduct || !dppDetail) return;
    setPublishing(true);
    try {
      await dppApi.publish(selectedProduct.id);
      setDppDetail(prev => prev ? { ...prev, status: 'published', published_at: new Date().toISOString() } : null);
      refetchDpps();
    } catch (err) {
      setGenError(err instanceof Error ? err.message : 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  const eudrLabel = (val: boolean | null) =>
    val === true  ? { text: 'Compliant ✓', color: T.sage } :
    val === false ? { text: 'Non-Compliant ✗', color: T.crimson } :
    { text: 'N/A', color: T.sub };

  return (
    <div className="fade-up">
      <SectionHeader
        title="EU Digital Product Passports"
        sub="ESPR-compliant DPPs generated from provenance data. Mandatory for EU market access from 2026."
        right={
          <div style={{ display: 'flex', gap: 8 }}>
            <Chip label="EU ESPR 2026" color={T.teal} />
            <Chip label={`${dpps.filter(d => d.status === 'published').length} Published`} color={T.sage} />
            <Chip label={`${missingDpps.length} Missing`} color={missingDpps.length > 0 ? T.crimson : T.sub} />
          </div>
        }
      />

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['all', 'published', 'draft', 'missing'].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)} style={{
            background: filterStatus === s ? `${T.teal}15` : T.panel,
            border: `1px solid ${filterStatus === s ? T.teal + '40' : T.border}`,
            color: filterStatus === s ? T.teal : T.sub,
            borderRadius: 8, padding: '7px 16px', cursor: 'pointer', fontSize: 12,
            fontWeight: filterStatus === s ? 700 : 400,
          }}>
            {s === 'all' ? `All (${dpps.length + missingDpps.length})`
              : s === 'missing' ? `Missing (${missingDpps.length})`
              : `${s.charAt(0).toUpperCase() + s.slice(1)} (${dpps.filter(d => d.status === s).length})`}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 20 }}>
        {/* Left: product list */}
        <div style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 280px)' }}>
          {dppsLoading ? [1,2,3,4].map(i => <div key={i} style={{ marginBottom: 8 }}><Skeleton h={64} /></div>) : null}

          {/* Products WITH DPPs */}
          {filterStatus !== 'missing' && filteredDpps.map(d => (
            <div key={d.product_id} onClick={() => {
              const p = products.find(pr => pr.id === d.product_id);
              if (p) { selectProduct(p); setDppDetail(d); }
            }} style={{
              background: selectedProduct?.id === d.product_id ? T.lift : T.panel,
              border: `1px solid ${selectedProduct?.id === d.product_id ? T.teal + '50' : T.border}`,
              borderLeft: `3px solid ${STATUS_C[d.status] || T.sub}`,
              borderRadius: 10, padding: '11px 14px', marginBottom: 8,
              cursor: 'pointer', transition: 'all .12s',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 20 }}>{d.img_emoji || '📦'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.product_name}</div>
                  <div style={{ fontSize: 10, color: T.sub }}>{d.dpp_id || '—'}</div>
                </div>
                <Chip label={d.status.toUpperCase()} color={STATUS_C[d.status] || T.sub} />
              </div>
            </div>
          ))}

          {/* Products WITHOUT DPPs */}
          {(filterStatus === 'all' || filterStatus === 'missing') && missingDpps.map(p => (
            <div key={p.id} onClick={() => selectProduct(p)} style={{
              background: selectedProduct?.id === p.id ? T.lift : T.panel,
              border: `1px solid ${selectedProduct?.id === p.id ? T.crimson + '40' : T.border}`,
              borderLeft: `3px solid ${T.crimson}`,
              borderRadius: 10, padding: '11px 14px', marginBottom: 8,
              cursor: 'pointer', transition: 'all .12s', opacity: .8,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 20 }}>{p.img_emoji}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                  <div style={{ fontSize: 10, color: T.sub }}>No DPP — click to generate</div>
                </div>
                <Chip label="MISSING" color={T.crimson} />
              </div>
            </div>
          ))}
        </div>

        {/* Right: DPP detail */}
        <div>
          {!selectedProduct ? (
            <Card style={{ textAlign: 'center', padding: '48px' }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>📋</div>
              <div style={{ color: T.sub, fontSize: 12 }}>Select a product to view or generate its EU Digital Product Passport</div>
            </Card>
          ) : (
            <Card>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 20, paddingBottom: 18, borderBottom: `1px solid ${T.border}` }}>
                <span style={{ fontSize: 36 }}>{selectedProduct.img_emoji}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: 18, color: T.text, marginBottom: 6 }}>{selectedProduct.name}</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {dppDetail ? (
                      <>
                        <Chip label={dppDetail.status.toUpperCase()} color={STATUS_C[dppDetail.status]} />
                        {dppDetail.dpp_id && <Chip label={dppDetail.dpp_id} color={T.teal} />}
                        {dppDetail.published_at && <Chip label={`Published ${new Date(dppDetail.published_at).toLocaleDateString()}`} color={T.sage} />}
                      </>
                    ) : (
                      <Chip label="NO DPP" color={T.crimson} />
                    )}
                  </div>
                </div>
                <ScoreRing score={selectedProduct.provenance_score} size={56} />
              </div>

              {genError && <ErrorBox message={genError} />}

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                <button onClick={generate} disabled={generating} style={{
                  background: generating ? T.border : `${T.teal}20`,
                  border: `1px solid ${T.teal}40`, color: generating ? T.sub : T.teal,
                  borderRadius: 9, padding: '9px 18px', cursor: generating ? 'not-allowed' : 'pointer',
                  fontSize: 12, fontWeight: 600,
                }}>
                  {generating ? 'Generating…' : dppDetail ? '↻ Refresh DPP' : '+ Generate DPP'}
                </button>
                {dppDetail && dppDetail.status === 'draft' && (
                  <button onClick={publish} disabled={publishing} style={{
                    background: publishing ? T.border : `${T.sage}20`,
                    border: `1px solid ${T.sage}40`, color: publishing ? T.sub : T.sage,
                    borderRadius: 9, padding: '9px 18px', cursor: publishing ? 'not-allowed' : 'pointer',
                    fontSize: 12, fontWeight: 600,
                  }}>
                    {publishing ? 'Publishing…' : '↑ Publish to EU Registry'}
                  </button>
                )}
              </div>

              {/* DPP data */}
              {dppDetail && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 18 }}>
                    {[
                      ['Origin Country', dppDetail.origin_country || '—'],
                      ['Category', dppDetail.product_category || '—'],
                      ['Recyclability', dppDetail.recyclability_pct != null ? `${dppDetail.recyclability_pct}%` : '—'],
                      ['Carbon Footprint', dppDetail.carbon_footprint_kg != null ? `${dppDetail.carbon_footprint_kg} kg CO₂` : 'Pending IoT data'],
                      ['Lot Numbers', dppDetail.lot_numbers?.length > 0 ? dppDetail.lot_numbers.join(', ') : 'None linked'],
                      ['Blockchain', dppDetail.blockchain_hash ? dppDetail.blockchain_hash.slice(0, 18) + '…' : 'Not anchored'],
                    ].map(([k, v]) => (
                      <div key={k} style={{ background: T.card, borderRadius: 8, padding: '10px 12px', border: `1px solid ${T.border}` }}>
                        <div style={{ fontSize: 9, color: T.sub, textTransform: 'uppercase', letterSpacing: .8, marginBottom: 4 }}>{k}</div>
                        <div style={{ fontSize: k === 'Blockchain' ? 10 : 12, color: T.text, fontFamily: k === 'Blockchain' || k === 'Lot Numbers' ? "'DM Mono', monospace" : 'inherit' }}>{v}</div>
                      </div>
                    ))}
                  </div>

                  {/* EUDR block */}
                  <div style={{
                    background: `${dppDetail.eudr_compliant === true ? T.sage : dppDetail.eudr_compliant === false ? T.crimson : T.sub}0a`,
                    border: `1px solid ${dppDetail.eudr_compliant === true ? T.sage : dppDetail.eudr_compliant === false ? T.crimson : T.sub}25`,
                    borderRadius: 10, padding: '14px 16px',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: T.sub }}>EU Deforestation Regulation (EUDR)</div>
                      <Chip label={eudrLabel(dppDetail.eudr_compliant).text} color={eudrLabel(dppDetail.eudr_compliant).color} />
                    </div>
                    <div style={{ fontSize: 11, color: T.sub, lineHeight: 1.6 }}>
                      {dppDetail.eudr_compliant === true
                        ? 'All suppliers verified. Origin geolocation data on file. Due diligence statement ready for submission.'
                        : dppDetail.eudr_compliant === false
                          ? 'One or more suppliers are unverified or lack origin geolocation data. EUDR compliance blocked.'
                          : 'Product category not in scope for EUDR (applies to beef, soy, palm oil, coffee, cocoa, rubber, wood).'}
                    </div>
                  </div>
                </>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
